/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { Box, Flex, Card, CardHeader, CardBody, Heading, Text, Button, ButtonGroup } from '@chakra-ui/react';

import { Modal } from '../atoms/modal';
import { ApproveDataIcon } from '../iconButtons/ApproveDataIcon';
import { EditDataIcon } from '../iconButtons/EditDataIcon';
import { UndoEdit } from '../iconButtons/UndoEdit';
import { RemoveItem } from '../iconButtons/RemoveItem';
import { slug } from '../../utilities/formatting';
import { ICardItem } from 'lib/types';

export interface ITheme {}

interface IProps {
  id: string;
  index: number;
  theme?: ITheme;
  modalTitle: string;
  modalBlurb?: React.ReactNode;
  additionalDataSelectorName?: string;
  cardTitle?: React.ReactNode;
  cardTitleRequired?: boolean;
  cardItems: Array<ICardItem>;
  showModal?: boolean;
  children: React.ReactNode;
  hasAddMoreButton?: boolean;
  addButtonLabel?: string;
  addAnotherButtonLabel?: string;
  hasAddFirstButton?: string;
  hasInCardActions?: boolean;
  hasRemoveCardAction?: boolean;
  importEnabled?: boolean;
  importApproved?: boolean;
  importUpdateEnabled?: boolean;
  importEdited?: boolean;
  onImportApprove?: (index: number) => void;
  onImportEdit?: (index: number) => void;
  onRemove?: (index: number) => void;
  toggleModal: (value: number) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  onImportUndo?: () => void;
  hideEditButton?: boolean;
}

const CardWithModal: React.FC<IProps> = (props: IProps) => {
  const {
    id,
    index,
    modalTitle,
    modalBlurb,
    additionalDataSelectorName,
    cardTitle,
    cardTitleRequired,
    onRemove,
    cardItems,
    showModal,
    hasAddMoreButton,
    addButtonLabel,
    addAnotherButtonLabel,
    hasAddFirstButton,
    children,
    toggleModal,
    importEnabled,
    importApproved,
    importUpdateEnabled,
    onImportApprove,
    hasInCardActions = true,
    hasRemoveCardAction = false,
    canUndo = false,
    onUndo,
    onImportUndo,
    // importEdited
    hideEditButton = false
  } = props;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const onToggleConfirmDeleteDialog = () => {
    setShowConfirmDialog(!showConfirmDialog);
  };

  const isCardIncomplete =
    (cardTitleRequired && !cardTitle) || cardItems.some((cardItem: ICardItem) => cardItem.required && !cardItem.value);

  return (
    <Flex gap={{ base: 1, lg: 2 }} data-selector={`${id}`}>
      {hasAddMoreButton && index > 0 ? (
        <Button
          id="add-another-value-button"
          data-selector="add-another-value-button"
          onClick={() => toggleModal(index)}
          variant="secondary"
          size="lg"
        >
          {addAnotherButtonLabel}
        </Button>
      ) : hasAddMoreButton || hasAddFirstButton ? (
        <Button
          id="add-value-button"
          data-selector="add-value-button"
          onClick={() => toggleModal(index)}
          variant="solid"
          size="lg"
        >
          {addButtonLabel || hasAddFirstButton}
        </Button>
      ) : (
        <>
          <Card flexGrow="1" variant="outline" borderRadius="lg" mb={3}>
            <Flex>
              <Box flexGrow="1">
                {!hasAddFirstButton && (
                  <>
                    {cardTitleRequired || cardTitle ? (
                      <CardHeader
                        data-selector={
                          additionalDataSelectorName
                            ? `card-title-${additionalDataSelectorName}-${index}`
                            : `card-title-${index}`
                        }
                        pb={0}
                      >
                        {cardTitleRequired && !cardTitle ? (
                          <>
                            <Heading as="h3" fontSize="lg" fontWeight="medium">
                              Title:
                            </Heading>

                            <Text
                              data-selector={
                                additionalDataSelectorName
                                  ? `${additionalDataSelectorName}-card-title-${index}`
                                  : `title-${index}`
                              }
                              mb={4}
                            >
                              <Box as="b" color="red.500" _dark={{ color: 'red.300' }}>
                                Required
                              </Box>
                            </Text>
                          </>
                        ) : (
                          <Heading as="h3" fontSize="lg" fontWeight="medium">
                            {cardTitle}
                          </Heading>
                        )}
                      </CardHeader>
                    ) : null}

                    <CardBody>
                      {cardItems.map((cardItem: ICardItem, cardItemIndex: number) => (
                        <Flex
                          flexDirection={{ base: 'column', lg: 'row' }}
                          columnGap={4}
                          key={
                            additionalDataSelectorName
                              ? `card-item-${additionalDataSelectorName}-${index}-${cardItemIndex}`
                              : `card-item-${index}-${cardItemIndex}`
                          }
                          data-selector={
                            additionalDataSelectorName
                              ? `card-item-${additionalDataSelectorName}-${index}-${cardItemIndex}`
                              : `card-item-${index}-${cardItemIndex}`
                          }
                          mb={6}
                          _last={{ mb: 0 }}
                        >
                          <Box
                            color="muted"
                            width={{ base: 'auto', lg: '12rem' }}
                            flexShrink="0"
                            fontWeight="medium"
                            whiteSpace="normal"
                          >
                            {cardItem.label}:
                          </Box>

                          <Box
                            data-selector={
                              additionalDataSelectorName
                                ? `${additionalDataSelectorName}-${slug(cardItem.label)}-${index}`
                                : `${slug(cardItem.label)}-${index}`
                            }
                          >
                            {cardItem.required && !cardItem.value ? (
                              <Box as="b" color="red.500" _dark={{ color: 'red.300' }}>
                                Required
                              </Box>
                            ) : (
                              cardItem.value
                            )}
                          </Box>
                        </Flex>
                      ))}
                    </CardBody>
                  </>
                )}
              </Box>

              {hasInCardActions && (
                <Flex pt={2} pr={2}>
                  <EditDataIcon
                    type="unclicked"
                    id={`edit-card-${index}`}
                    onClick={() => toggleModal(index)}
                    size="md"
                    variant="ghost"
                  />

                  <RemoveItem
                    data-selector={`remove-card-${index}`}
                    onClick={onToggleConfirmDeleteDialog}
                    size="md"
                    variant="ghost"
                  />
                </Flex>
              )}
            </Flex>
          </Card>

          {importEnabled && (
            <>
              {!importUpdateEnabled && !isCardIncomplete && (
                <ApproveDataIcon
                  id={`card-${index}-import-approve`}
                  type={importApproved ? 'clicked' : 'unclicked'}
                  onClick={!importApproved && onImportApprove ? () => onImportApprove(index) : undefined}
                />
              )}

              {!hideEditButton && (
                <EditDataIcon
                  id={`card-${index}-import-edit`}
                  // eslint-disable-next-line no-nested-ternary
                  type={importApproved ? 'focus-off' : isCardIncomplete ? 'warning' : 'unclicked'}
                  onClick={() => toggleModal(index)}
                />
              )}

              {hasRemoveCardAction && (
                <RemoveItem data-selector={`remove-card-${index}`} onClick={onToggleConfirmDeleteDialog} />
              )}
            </>
          )}
        </>
      )}

      {canUndo && (
        <UndoEdit
          id={id}
          onUndo={() => {
            if (importEnabled) {
              onImportUndo && onImportUndo();
            } else {
              onUndo && onUndo();
            }
          }}
        />
      )}

      {showModal ? (
        <Modal
          id={id}
          title={modalTitle}
          show={true}
          onClose={() => toggleModal(index)}
          description={modalBlurb}
          hideFooter
        >
          {children}
        </Modal>
      ) : null}

      {showConfirmDialog ? (
        <Modal
          id={`confirm-delete-${index}`}
          title={'Are you sure you want to remove this item?'}
          onClose={onToggleConfirmDeleteDialog}
          show={true}
          size="lg"
          footer={
            <ButtonGroup spacing={4}>
              <Button
                id={`confirm-delete-${index}-no`}
                data-selector={`no-delete-${index}`}
                onClick={onToggleConfirmDeleteDialog}
              >
                Cancel
              </Button>

              <Button
                id={`confirm-delete-${index}-yes`}
                data-selector={`confirm-delete-${index}-yes`}
                onClick={() => {
                  onRemove!(index);
                  onToggleConfirmDeleteDialog();
                }}
                variant="solid"
                colorScheme="red"
              >
                Yes, remove item
              </Button>
            </ButtonGroup>
          }
        />
      ) : null}
    </Flex>
  );
};

export { CardWithModal };
